import React, { createContext, useState, useContext, useCallback } from 'react';
import api from '../utils/apiConfig';

const DeliveryContext = createContext();

export const useDelivery = () => {
  const context = useContext(DeliveryContext);
  if (!context) {
    throw new Error('useDelivery must be used within a DeliveryProvider');
  }
  return context;
};

export const DeliveryProvider = ({ children }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    pendingDeliveries: 0,
    inTransitDeliveries: 0,
    deliveredDeliveries: 0,
    failedDeliveries: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Load deliveries with filters
  const loadDeliveries = useCallback(async (locationId, filters = {}, page = 1, limit = 20) => {
    if (!locationId) {
      setError('Location ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        locationId,
        page,
        limit,
        ...filters
      };

      // Remove empty string values to avoid validation errors
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await api.get('/deliveries', { params });

      if (response.data.success) {
        setDeliveries(response.data.data.deliveries);
        setPagination(response.data.data.pagination);
      } else {
        setError(response.data.message || 'Failed to load deliveries');
      }
    } catch (err) {
      console.error('Load deliveries error:', err);
      setError(err.response?.data?.message || 'Failed to load deliveries');
      // Set empty deliveries array on error
      setDeliveries([]);
      setPagination({
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: limit,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Get single delivery
  const getDelivery = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/deliveries/${id}`);

      if (response.data.success) {
        return response.data.data.delivery;
      } else {
        setError(response.data.message || 'Failed to load delivery');
        return null;
      }
    } catch (err) {
      console.error('Get delivery error:', err);
      setError(err.response?.data?.message || 'Failed to load delivery');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create delivery
  const createDelivery = useCallback(async (deliveryData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/deliveries', deliveryData);

      if (response.data.success) {
        // Add new delivery to list
        setDeliveries(prev => [response.data.data.delivery, ...prev]);
        return response.data.data.delivery;
      } else {
        setError(response.data.message || 'Failed to create delivery');
        return null;
      }
    } catch (err) {
      console.error('Create delivery error:', err);
      setError(err.response?.data?.message || 'Failed to create delivery');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update delivery
  const updateDelivery = useCallback(async (id, updateData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.put(`/deliveries/${id}`, updateData);

      if (response.data.success) {
        // Update delivery in list
        setDeliveries(prev =>
          prev.map(d => d.id === id ? response.data.data.delivery : d)
        );
        return response.data.data.delivery;
      } else {
        setError(response.data.message || 'Failed to update delivery');
        return null;
      }
    } catch (err) {
      console.error('Update delivery error:', err);
      setError(err.response?.data?.message || 'Failed to update delivery');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update delivery status
  const updateDeliveryStatus = useCallback(async (id, status) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.put(`/deliveries/${id}/status`, { status });

      if (response.data.success) {
        // Update delivery in list
        setDeliveries(prev =>
          prev.map(d => d.id === id ? response.data.data.delivery : d)
        );
        return response.data.data.delivery;
      } else {
        setError(response.data.message || 'Failed to update status');
        return null;
      }
    } catch (err) {
      console.error('Update status error:', err);
      setError(err.response?.data?.message || 'Failed to update status');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update delivery GPS location
  const updateDeliveryLocation = useCallback(async (id, latitude, longitude) => {
    try {
      const response = await api.put(`/deliveries/${id}/location`, {
        latitude,
        longitude
      });

      if (response.data.success) {
        // Update delivery in list
        setDeliveries(prev =>
          prev.map(d => d.id === id ? response.data.data.delivery : d)
        );
        return response.data.data.delivery;
      }
      return null;
    } catch (err) {
      console.error('Update location error:', err);
      return null;
    }
  }, []);

  // Upload proof of delivery
  const uploadProof = useCallback(async (id, file) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('proof', file);

      const response = await api.post(`/deliveries/${id}/proof`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Update delivery in list
        setDeliveries(prev =>
          prev.map(d => d.id === id ? response.data.data.delivery : d)
        );
        return response.data.data.delivery;
      } else {
        setError(response.data.message || 'Failed to upload proof');
        return null;
      }
    } catch (err) {
      console.error('Upload proof error:', err);
      setError(err.response?.data?.message || 'Failed to upload proof');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete delivery
  const deleteDelivery = useCallback(async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.delete(`/deliveries/${id}`);

      if (response.data.success) {
        // Remove delivery from list
        setDeliveries(prev => prev.filter(d => d.id !== id));
        return true;
      } else {
        setError(response.data.message || 'Failed to delete delivery');
        return false;
      }
    } catch (err) {
      console.error('Delete delivery error:', err);
      setError(err.response?.data?.message || 'Failed to delete delivery');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load delivery statistics
  const loadStats = useCallback(async (locationId) => {
    if (!locationId) return;

    try {
      const response = await api.get(`/deliveries/stats/${locationId}`);

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Load stats error:', err);
      // Set default stats if API fails
      setStats({
        totalDeliveries: 0,
        pendingDeliveries: 0,
        inTransitDeliveries: 0,
        deliveredDeliveries: 0,
        failedDeliveries: 0,
        statusBreakdown: []
      });
    }
  }, []);

  // Change page
  const changePage = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  }, []);

  // Update filters
  const updateFilters = useCallback((filters) => {
    // This will trigger a reload in the component
    return filters;
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    deliveries,
    stats,
    loading,
    error,
    pagination,
    loadDeliveries,
    getDelivery,
    createDelivery,
    updateDelivery,
    updateDeliveryStatus,
    updateDeliveryLocation,
    uploadProof,
    deleteDelivery,
    loadStats,
    changePage,
    updateFilters,
    clearError
  };

  return (
    <DeliveryContext.Provider value={value}>
      {children}
    </DeliveryContext.Provider>
  );
};

export default DeliveryContext;
